import { useMutation } from '@apollo/client/react';
import { ConfirmationDialog } from '@optools/components/dialog/confirmation-dialog';
import { InfoButton, InfoButtonTrigger } from '@optools/components/info-button';
import { UPDATE_PIPELINE_MEDIA } from '@optools/services/operator-gateway/btc/mutations';
import { BtcSchedulePipeline } from '@optools/services/operator-gateway/btc/types';
import { getMediaId } from '@optools/utils/pipeline';
import { ArrowLeftRight } from 'lucide-react';
import { toast } from 'sonner';

export interface UpdatePipelineMediaButtonProps {
  pipeline: BtcSchedulePipeline;
}

export function UpdatePipelineMediaButton({ pipeline }: UpdatePipelineMediaButtonProps) {
  const mediaId = getMediaId(pipeline);
  const [updatePipelineMediaMutation, { loading }] = useMutation(UPDATE_PIPELINE_MEDIA, {
    onCompleted: ({ updatePipelineMedia }) => {
      if (updatePipelineMedia?.success) {
        toast.success('Update Pipeline Media Success', {
          description: (
            <>
              <h2 className="mb-1">Update Pipeline Media</h2>
              <p>{`Updated pipeline ${pipeline.id}`}</p>
            </>
          ),
        });
      } else {
        toast.error('Update Pipeline Media Error', {
          description: (
            <div className="flex-col">
              <h2 className="mb-1">Failed to Update Pipeline Media</h2>
              <p>{`Failed to update pipeline media for ${updatePipelineMedia?.mediaId ?? mediaId}`}</p>
              {updatePipelineMedia?.reason && updatePipelineMedia.reason.length > 0 && (
                <p>{`Reason: ${updatePipelineMedia?.reason}`}</p>
              )}
            </div>
          ),
          duration: Infinity,
        });
      }
    },
  });

  return (
    <InfoButton
      description={
        <div className="space-y-2 text-xs">
          <p>Updates mediaId to an existing pipeline.</p>
          <p>
            The gateway will fetch the relevant game and feed data based on the mediaId passed in
            and request a pipeline update from Skywalker.
          </p>
        </div>
      }
      size="sm">
      <ConfirmationDialog
        description="Are you sure you want to update the pipeline media?"
        title="Update Pipeline Media"
        onConfirm={() => updatePipelineMediaMutation({ variables: { mediaId } })}>
        <InfoButtonTrigger disabled={loading} loading={loading}>
          <ArrowLeftRight />
          Update Pipeline Media
        </InfoButtonTrigger>
      </ConfirmationDialog>
    </InfoButton>
  );
}
